/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Pressable, Icon, Text, Label, Tooltip } from '@components/atoms'

import {
	useAppDispatch,
	useAppSelector,
	EditMenuType,
	adminActions,
	AdminStateType,
} from '@redux/index'
import { grey } from '@mui/material/colors'

interface PropsType {
	readOnly?: boolean
}

export const AdminEditMenuIcon = (props: PropsType) => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const editTempMenu: EditMenuType = useAppSelector(state => state.admin.editTempMenu)
	const editMenuMode: AdminStateType['editMenuMode'] = useAppSelector(
		state => state.admin.editMenuMode,
	)

	// state
	const [isHovering, setIsHovering] = React.useState<boolean>(false)

	// variables
	const readOnly: boolean = props.readOnly ?? false
	const disabled: boolean = editMenuMode === 'update' && editTempMenu.id === ''

	const onOpenModal = () => {
		reduxDispatch(adminActions.MENU_OPEN_MODAL('iconPicker'))
	}

	const onMouseOver = React.useCallback(() => {
		setIsHovering(true)
	}, [])

	const onMouseLeave = React.useCallback(() => {
		setIsHovering(false)
	}, [])

	const onRemoveIcon = () => {
		const newMenu: EditMenuType = {
			...editTempMenu,
			icon: '',
		}

		reduxDispatch(adminActions.EDIT_TEMP_MENU({ ...newMenu }))
	}

	return (
		<View css={style}>
			<Label className="createMenuLabel icon">아이콘</Label>

			<View
				className={`menuIconView ${editTempMenu.icon === '' ? 'empty' : ''} ${
					disabled ? 'disabled' : ''
				}`}
			>
				<Tooltip title="아이콘 변경을 원하시면 클릭하세요.">
					<Pressable
						className={`iconButton ${editTempMenu.icon === '' ? 'empty' : ''} ${
							disabled ? 'disabled' : ''
						} ${readOnly ? 'readOnly' : ''}`}
						onClick={onOpenModal}
						onMouseOver={onMouseOver}
						onMouseLeave={onMouseLeave}
						disabled={disabled || readOnly}
					>
						<Icon name={editTempMenu.icon} className="icon" />
					</Pressable>
				</Tooltip>

				{editTempMenu.icon === '' ? (
					<View className="addIconView">
						<Text className="addIconViewText" hint={disabled ? '300' : '500'}>
							아이콘 추가
						</Text>
						<Icon name="plus" className="addIconViewIcon" />
					</View>
				) : (
					<Tooltip title="아이콘 삭제">
						<Pressable
							className="removeIconButton"
							css={{ opacity: isHovering ? 1 : 0.2 }}
							onClick={onRemoveIcon}
						>
							<Icon name="close" className="removeIcon" />
							<Text category="p2" className="removeIconText">
								삭제
							</Text>
						</Pressable>
					</Tooltip>
				)}
			</View>
		</View>
	)
}

const style = css({
	position: 'relative',
	width: '100%',
	height: '9rem',
	paddingTop: '1rem',

	'.createMenuLabel': {
		position: 'absolute',
		zIndex: 4,
		top: '0.3rem',
		left: '0.3rem',
	},

	'.menuIconView': {
		position: 'relative',
		width: '8rem',
		height: '8rem',
		padding: '0.5rem',
		margin: 'auto',
		background: grey[100],
		borderRadius: '50%',
		border: `5px solid ${grey[200]}`,

		'&.disabled': {
			background: grey[200],

			'.addIconViewIcon': {
				color: grey[300],
			},
		},

		'.iconButton': {
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			width: '100%',
			height: '100%',

			'&.empty': {
				zIndex: 3,
				border: `4px dashed ${grey[300]}`,
				borderRadius: '50%',

				'&:hover:not(.disabled)': {
					border: `4px dashed ${grey[600]}`,
				},
			},

			'.icon': {
				fontSize: '90px',
			},
		},

		'&.empty': {
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',

			'.addIconView': {
				position: 'absolute',
				zIndex: 2,
				top: '2rem',

				'.addIconViewText': {
					margin: 0,
				},

				'.addIconViewIcon': {
					fontSize: '30px',
				},
			},
		},

		'.removeIconButton': {
			position: 'absolute',
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			width: '3.5rem',
			bottom: '-.5rem',
			right: '0rem',
			left: 0,
			margin: 'auto',
			borderRadius: '20px',
			background: grey[900],
			color: '#FFF',

			'.removeIcon': {
				color: '#fff',
				fontSize: '14px',
			},

			'.removeIconText': {
				margin: 0,
			},

			'&:hover': {
				opacity: 1,
			},
		},
	},
})
